import React, { useState, useEffect, useRef, useCallback } from 'react'
import AutoResizeTextarea from '../shared/AutoResizeTextarea'
import DynamicFormFields from '../shared/DynamicFormFields'
import Calendar from '../Availability/Calendar'
import TimeSlotList from '../Availability/TimeSlotList'
import TimezoneSelector from '../Booking/TimezoneSelector'
import { submitConsultation } from '../../utils/teachingTeamApi'
import { saveTimezoneToServer, getCurrentUserLocale, isInAppBrowser } from 'utils'
import { trackEvent } from 'utils/analytics'
import { useModalBehavior } from 'hooks'
import InAppBrowserWarningModal from '../shared/InAppBrowserWarningModal'

export default function ConsultationFormModal({
  isOpen,
  onClose,
  onSubmitted,
  teamId,
  teamSlug,
  fields,
  colors,
  currentUser,
  translations,
  consultationEnabled = false,
  consultationDuration,
  userTimezone,
  previousResponses,
  mode,
  courseContext
}) {
  const [responses, setResponses] = useState(previousResponses || {})
  const [consultationMessage, setConsultationMessage] = useState('')
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [slotError, setSlotError] = useState(null)
  const [timezone, setTimezone] = useState(userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Taipei')

  // Guest fields
  const [guestEmail, setGuestEmail] = useState('')
  const [guestName, setGuestName] = useState('')

  // Active consultation guard (only for logged-in users)
  const [activeConsultation, setActiveConsultation] = useState(null)
  const [checkingActive, setCheckingActive] = useState(false)

  // Join mode: keep OAuth for teachers
  const [showInAppWarning, setShowInAppWarning] = useState(false)

  // Guest email confirmation screen
  const [confirmingEmail, setConfirmingEmail] = useState(false)

  // Two-step form: Step 1 = time selection, Step 2 = form fields
  const [currentStep, setCurrentStep] = useState(1)

  // Calendar + TimeSlot state
  const [availabilityData, setAvailabilityData] = useState({})
  const [selectedDate, setSelectedDate] = useState(null)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [loadingSlots, setLoadingSlots] = useState(false)
  const timeSlotsRef = useRef(null)
  const actionsRef = useRef(null)
  const submittingRef = useRef(false)

  const t = translations?.consultation || {}
  const at = translations?.availability || {}
  const primaryColor = colors?.primary || '#3366FF'
  const hasFields = fields && fields.length > 0
  const isJoinMode = mode === 'join'
  const isGuest = !currentUser && !isJoinMode

  // When consultationEnabled is false, skip Step 1 — always show Step 2
  const effectiveStep = consultationEnabled ? currentStep : 2

  // Join mode: override text for prospective teachers
  const joinText = {
    title: t.join_title || 'Chat with Team Leader',
    pitch: t.join_pitch || 'Want to learn more? Schedule a time to chat about collaboration and team culture.',
    subtitle: t.join_subtitle || 'Fill in the details below and we\'ll get back to you soon',
    messageLabel: t.join_message_label || 'Self Introduction',
    messagePlaceholder: t.join_message_placeholder || 'Tell us about yourself, or what you\'d like to know?',
  }

  // Reset all state when modal opens
  useEffect(() => {
    if (!isOpen) return
    setCurrentStep(1)
    setResponses(previousResponses || {})
    setConsultationMessage(courseContext ? (t.course_interest_prefill || '對 %{course_name} 有興趣').replace('%{course_name}', courseContext) : '')
    setErrors({})
    submittingRef.current = false
    setSubmitting(false)
    setSelectedSlot(null)
    setSlotError(null)
    setSelectedDate(null)
    setAvailabilityData({})
    setGuestEmail('')
    setGuestName('')
    setActiveConsultation(null)
    setCheckingActive(false)
    setConfirmingEmail(false)
  }, [isOpen])

  // Fetch consultation slots
  const fetchSlots = useCallback(async (tz = timezone, monthDate = null) => {
    setLoadingSlots(true)
    setSelectedSlot(null)
    try {
      const params = new URLSearchParams({ timezone: tz })
      if (monthDate) {
        const pad = (n) => String(n).padStart(2, '0')
        const startDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
        const endDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0)
        params.set('start_date', `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`)
        params.set('end_date', `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`)
      }
      const response = await fetch(`/api/teaching_teams/${teamId}/consultation_slots?${params}`)
      if (!response.ok) throw new Error('Failed to load slots')
      const json = await response.json()
      const grouped = json.data || {}
      setAvailabilityData(grouped)

      if (!monthDate) {
        const firstDateKey = Object.keys(grouped).filter(k => grouped[k].length > 0).sort()[0]
        if (firstDateKey) {
          const [y, m, d] = firstDateKey.split('-').map(Number)
          setSelectedDate(new Date(y, m - 1, d))
          setCurrentMonth(new Date(y, m - 1, 1))
        }
      }
    } catch (err) {
      setAvailabilityData({})
    } finally {
      setLoadingSlots(false)
    }
  }, [teamId, timezone])

  const checkActiveConsultation = useCallback(async () => {
    setCheckingActive(true)
    try {
      const response = await fetch(`/api/teaching_teams/${teamId}/consultation_check`)
      if (!response.ok) {
        fetchSlots()
        return
      }
      const data = await response.json()
      if (data.has_active) {
        setActiveConsultation(data)
      } else {
        setActiveConsultation(null)
        fetchSlots()
      }
    } catch {
      fetchSlots()
    } finally {
      setCheckingActive(false)
    }
  }, [teamId, fetchSlots])

  // Fetch slots when modal opens (if consultation scheduling enabled)
  useEffect(() => {
    if (!isOpen || !consultationEnabled) return

    if (currentUser) {
      checkActiveConsultation()
    } else {
      fetchSlots()
    }
  }, [isOpen, consultationEnabled])

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setSelectedSlot(null)
    setTimeout(() => {
      if (timeSlotsRef.current) {
        timeSlotsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 150)
  }

  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth)
    setSelectedDate(null)
    setSelectedSlot(null)
    fetchSlots(timezone, newMonth)
  }

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot)
    setSlotError(null)
    trackEvent('consultation_time_selected', { team_slug: teamSlug })
    setTimeout(() => {
      if (actionsRef.current) {
        actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    }, 150)
  }

  const selectedDateSlots = (() => {
    if (!selectedDate) return []
    const pad = (n) => String(n).padStart(2, '0')
    const dateKey = `${selectedDate.getFullYear()}-${pad(selectedDate.getMonth() + 1)}-${pad(selectedDate.getDate())}`
    return availabilityData[dateKey] || []
  })()

  const wrappedOnClose = confirmingEmail ? () => setConfirmingEmail(false) : onClose
  const { handleOverlayClick } = useModalBehavior(isOpen, wrappedOnClose)

  if (!isOpen) return null

  const handleChange = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }))
    if (errors[fieldId]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[fieldId]
        return next
      })
    }
  }

  const validate = () => {
    const newErrors = {}

    // Guest fields validation
    if (isGuest) {
      if (!guestEmail.trim()) {
        newErrors._email = t.email_required || 'Email is required'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim())) {
        newErrors._email = t.email_invalid || 'Please enter a valid email'
      }
      if (!guestName.trim()) {
        newErrors._name = t.name_required || 'Name is required'
      }
    }

    if (fields && fields.length > 0) {
      fields.forEach(field => {
        if (field.required && !responses[field.id]?.trim()) {
          newErrors[field.id] = true
        }
      })
    }
    if (!consultationMessage.trim()) {
      newErrors._message = true
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Join mode OAuth trigger (kept only for teacher join flow)
  const triggerGoogleOAuth = () => {
    if (isInAppBrowser()) {
      setShowInAppWarning(true)
      return
    }
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/users/auth/google_oauth2'
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken
    form.appendChild(csrfInput)
    const roleInput = document.createElement('input')
    roleInput.type = 'hidden'
    roleInput.name = 'role'
    roleInput.value = 'teacher'
    form.appendChild(roleInput)
    document.body.appendChild(form)
    form.submit()
  }

  const doSubmit = async (startTime = null) => {
    if (submittingRef.current) return
    submittingRef.current = true
    trackEvent('consultation_form_completed', { team_slug: teamSlug, mode: isJoinMode ? 'join' : 'consult' })
    setSubmitting(true)
    setSlotError(null)

    // Join mode without login → OAuth
    if (!currentUser && isJoinMode) {
      triggerGoogleOAuth()
      return
    }

    try {
      const result = await submitConsultation(teamId, responses, consultationMessage, {
        startTime,
        source: isJoinMode ? 'join' : null,
        email: isGuest ? guestEmail.trim() : null,
        name: isGuest ? guestName.trim() : null,
        timezone: isGuest ? timezone : null
      })
      if (onSubmitted) onSubmitted(result, { hasBooking: !!startTime })
    } catch (err) {
      if (startTime && err.message?.includes('unavailable')) {
        setSlotError(t.slot_unavailable || 'This slot is no longer available. Please select another time.')
        setSelectedSlot(null)
        setCurrentStep(1)
      } else {
        alert(err.message || t.error || 'Failed to submit. Please try again.')
      }
    } finally {
      submittingRef.current = false
      setSubmitting(false)
      setConfirmingEmail(false)
    }
  }

  const handleStep1Next = () => {
    trackEvent('consultation_step1_completed', { team_slug: teamSlug })
    setCurrentStep(2)
  }

  const handleStep2Back = () => {
    setCurrentStep(1)
  }

  const handleSubmit = () => {
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector('.border-red-400')
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
      return
    }
    if (consultationEnabled && !selectedSlot) return
    if (isGuest) {
      setConfirmingEmail(true)
      return
    }
    doSubmit(selectedSlot?.start_time || null)
  }

  const headerTitle = isJoinMode ? joinText.title : (t.title || 'Free Consultation')

  // Step indicator text
  const stepLabel = consultationEnabled
    ? (effectiveStep === 1
        ? (t.step1_label || 'Step 1: Select a time')
        : (t.step2_label || 'Step 2: Tell us about you'))
    : null

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={handleOverlayClick}
    >
      <div
        className={`bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col transition-all ${effectiveStep === 1 ? 'lg:max-w-3xl' : 'lg:max-w-lg'}`}
      >
        {/* Mobile header — back arrow */}
        <div className="lg:hidden px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-3 shrink-0">
          <button onClick={wrappedOnClose} className="flex items-center text-gray-600 cursor-pointer" aria-label="Back">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-gray-900 truncate">{headerTitle}</span>
        </div>
        {/* Desktop header — blue bar */}
        <div
          className="hidden lg:flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ backgroundColor: '#E6F0FF' }}
        >
          <span className="font-semibold" style={{ color: '#3366FF' }}>{headerTitle}</span>
          <button onClick={wrappedOnClose} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {confirmingEmail ? (
          <>
            {/* Email confirmation screen — independent render state */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="max-w-sm mx-auto py-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t.email_confirm?.title || 'Confirm your Email'}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {t.email_confirm?.subtitle || "We'll send the consultation reply to"}
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-3">
                  <p className="text-base font-semibold text-gray-900 break-all">{guestEmail}</p>
                </div>
                {consultationEnabled && selectedSlot && selectedDate && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-3">
                    <p className="text-sm text-blue-800">
                      {selectedDate.toLocaleDateString(getCurrentUserLocale() || 'zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long', timeZone: timezone })} {selectedSlot.start}
                    </p>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-3">
                  {t.email_confirm?.warning || 'Please make sure the email is correct — this is the only way the teacher can reach you'}
                </p>
              </div>
            </div>
            <div className="shrink-0 px-5 py-4 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmingEmail(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
              >
                {t.email_confirm?.edit_button || 'Edit'}
              </button>
              <button
                type="button"
                onClick={() => doSubmit(selectedSlot?.start_time || null)}
                disabled={submitting}
                className="px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors cursor-pointer disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {submitting
                  ? (t.submitting || 'Submitting...')
                  : (t.email_confirm?.confirm_button || 'Confirm & Submit')
                }
              </button>
            </div>
          </>
        ) : effectiveStep === 1 ? (
          <>
            {/* Step 1: Time Selection */}
            <div className="p-6 overflow-y-auto flex-1">
              {stepLabel && (
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">{stepLabel}</p>
              )}

              {/* Pitch card */}
              <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: '#FFF8F0', borderLeft: `3px solid ${primaryColor}` }}>
                <p className="text-sm text-gray-600">
                  {isJoinMode ? joinText.pitch : (translations?.consultationNudge?.pitch || '很多學生錯估了自己的程度和需求，先跟我們聊聊，讓我們幫你推薦適合的課程和老師。')}
                </p>
              </div>

              {activeConsultation ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                  <svg className="w-10 h-10 text-amber-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-amber-800 font-medium mb-1">
                    {t.already_active || 'You already have an active consultation'}
                  </p>
                  <p className="text-sm text-amber-700 mb-3">
                    {(() => {
                      const now = new Date()
                      const startTime = new Date(activeConsultation.start_time)
                      const endTime = new Date(activeConsultation.end_time)
                      const timeStr = startTime.toLocaleString(getCurrentUserLocale() || 'zh-TW', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                        hour12: false,
                        timeZone: timezone
                      })
                      let label
                      if (now < startTime) label = t.consultation_upcoming || 'Upcoming'
                      else if (now < endTime) label = t.consultation_in_progress || 'In progress'
                      else label = t.consultation_pending_completion || 'Pending completion'
                      return `${timeStr}（${label}）`
                    })()}
                  </p>
                  <a
                    href={activeConsultation.can_reschedule
                      ? `/student/bookings/${activeConsultation.booking_id}/reschedule`
                      : `/student/bookings/${activeConsultation.booking_id}`}
                    className="inline-flex items-center px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {activeConsultation.can_reschedule
                      ? (t.reschedule_existing || 'Reschedule existing consultation')
                      : (t.view_existing || 'View existing consultation')}
                  </a>
                </div>
              ) : checkingActive ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  {slotError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
                      <p className="text-sm text-red-700">{slotError}</p>
                    </div>
                  )}

                  <div className="mb-4">
                    <TimezoneSelector
                      userTimezone={timezone}
                      onTimezoneChange={(tz) => {
                        setTimezone(tz)
                        setSelectedSlot(null)
                        setSelectedDate(null)
                        saveTimezoneToServer(tz)
                        fetchSlots(tz)
                      }}
                    />
                  </div>

                  {loadingSlots ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Calendar
                        availabilityData={availabilityData}
                        selectedDate={selectedDate}
                        onDateSelect={handleDateSelect}
                        currentMonth={currentMonth}
                        onMonthChange={handleMonthChange}
                        translations={at?.calendar || {}}
                      />
                      <div ref={timeSlotsRef}>
                        <TimeSlotList
                          slots={selectedDateSlots}
                          selectedDate={selectedDate}
                          selectedSlot={selectedSlot?.start || null}
                          onSlotSelect={handleSlotSelect}
                          duration={consultationDuration}
                          translations={at?.time_slots || {}}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Step 1 Footer */}
            {!activeConsultation && !checkingActive && (
              <div ref={actionsRef} className="shrink-0 px-5 py-4 border-t flex justify-end gap-3">
                <button type="button" onClick={wrappedOnClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer hidden lg:block">
                  {t.cancel || 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleStep1Next}
                  disabled={!selectedSlot}
                  className="px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors cursor-pointer disabled:opacity-50 w-full lg:w-auto"
                  style={{ backgroundColor: primaryColor }}
                >
                  {t.step1_next || '選好了，下一步'}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Step 2: Form Fields */}
            <div className="p-6 overflow-y-auto flex-1">
              {stepLabel && (
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">{stepLabel}</p>
              )}

              {/* Show selected time summary when coming from Step 1 */}
              {consultationEnabled && selectedSlot && selectedDate && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-blue-800">
                      {selectedDate.toLocaleDateString(getCurrentUserLocale() || 'zh-TW', { month: 'long', day: 'numeric', weekday: 'short', timeZone: timezone })} {selectedSlot.start}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleStep2Back}
                    className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer font-medium"
                  >
                    {t.change_time || '更改'}
                  </button>
                </div>
              )}

              {/* Pitch card — only show when no step label (consultationEnabled=false) */}
              {!consultationEnabled && (
                <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: '#FFF8F0', borderLeft: `3px solid ${primaryColor}` }}>
                  <p className="text-sm text-gray-600">
                    {isJoinMode ? joinText.pitch : (translations?.consultationNudge?.pitch || '很多學生錯估了自己的程度和需求，先跟我們聊聊，讓我們幫你推薦適合的課程和老師。')}
                  </p>
                </div>
              )}

              {!consultationEnabled && (
                <p className="text-sm text-gray-500 mb-5">
                  {isJoinMode ? joinText.subtitle : (t.subtitle || 'Fill in the information below and we\'ll respond as soon as possible')}
                </p>
              )}

              {/* Guest: email + name fields */}
              {isGuest && (
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.email_label || 'Email'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => {
                        setGuestEmail(e.target.value)
                        if (errors._email) setErrors(prev => { const next = { ...prev }; delete next._email; return next })
                      }}
                      placeholder={t.email_placeholder || 'your@email.com'}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 ${errors._email ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {errors._email && <p className="text-red-500 text-xs mt-1">{errors._email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.name_label || 'Name'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => {
                        setGuestName(e.target.value)
                        if (errors._name) setErrors(prev => { const next = { ...prev }; delete next._name; return next })
                      }}
                      placeholder={t.name_placeholder || 'How should we call you?'}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 ${errors._name ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    {errors._name && <p className="text-red-500 text-xs mt-1">{errors._name}</p>}
                  </div>
                </div>
              )}

              {/* Dynamic intake form fields */}
              {hasFields && (
                <div className="mb-4">
                  <DynamicFormFields
                    fields={fields}
                    responses={responses}
                    errors={errors}
                    onChange={handleChange}
                    translations={{ pleaseSelect: t.pleaseSelect, fieldRequired: t.fieldRequired }}
                  />
                </div>
              )}

              {/* Consultation message — label updated to "Learning needs / goal" */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isJoinMode ? joinText.messageLabel : (t.learning_needs_label || t.message_label || '學習需求/目標')} <span className="text-red-500">*</span>
                </label>
                <AutoResizeTextarea
                  value={consultationMessage}
                  onChange={(e) => {
                    setConsultationMessage(e.target.value)
                    if (errors._message) {
                      setErrors(prev => { const next = { ...prev }; delete next._message; return next })
                    }
                  }}
                  placeholder={isJoinMode ? joinText.messagePlaceholder : (t.learning_needs_placeholder || t.message_placeholder || '例如：準備多益考試，想加強聽力和閱讀')}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none ${
                    errors._message ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                {errors._message && (
                  <p className="text-red-500 text-xs mt-1">{t.message_required || 'Please fill in your learning needs'}</p>
                )}
              </div>
            </div>

            {/* Step 2 Footer */}
            <div ref={actionsRef} className="shrink-0 px-5 py-4 border-t flex justify-between gap-3">
              <div>
                {consultationEnabled && (
                  <button
                    type="button"
                    onClick={handleStep2Back}
                    disabled={submitting}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer disabled:opacity-50"
                  >
                    {t.step2_back || '上一步'}
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={wrappedOnClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer hidden lg:block">
                  {t.cancel || 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-6 py-2 rounded-lg text-white text-sm font-medium transition-colors cursor-pointer disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {submitting
                    ? (t.submitting || 'Submitting...')
                    : (!currentUser && isJoinMode)
                      ? (t.login_to_continue || 'Sign in to continue')
                      : consultationEnabled
                        ? (t.book_and_submit || '預約並送出')
                        : (t.submit_button || 'Submit')
                  }
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Keep InAppBrowserWarning only for join mode OAuth */}
      {isJoinMode && (
        <InAppBrowserWarningModal
          isOpen={showInAppWarning}
          onClose={() => setShowInAppWarning(false)}
          translations={translations?.in_app_browser}
        />
      )}
    </div>
  )
}
